import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import { CheckCircle2, Send } from "lucide-react";

export default function Contribute() {
  const [params] = useSearchParams();
  const [pages, setPages] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    page_slug: params.get("slug") || "",
    author_name: "",
    author_email: "",
    title: "",
    body: "",
  });

  useEffect(() => {
    client.get("/wiki/pages").then(r => setPages(r.data)).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.author_name || !form.title || !form.body) {
      setError("Name, title and body are required.");
      return;
    }
    try {
      await client.post("/contributions", form);
      setSubmitted(true);
    } catch {
      setError("Could not submit. Please try again.");
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-16 md:py-24 text-center" data-testid="contribute-success">
        <CheckCircle2 className="w-14 h-14 md:w-16 md:h-16 text-[var(--t-green)] mx-auto mb-4" />
        <h1 className="font-display text-3xl md:text-4xl text-[var(--t-cyan)] uppercase mb-3 leading-tight">Transmission Received</h1>
        <p className="text-sm md:text-base text-[var(--t-text-dim)] mb-6">Your contribution is in the moderation queue. A Citizen moderator will review it shortly. Thank you for fortifying the codex.</p>
        <button onClick={() => { setSubmitted(false); setForm({ page_slug: "", author_name: "", author_email: "", title: "", body: "" }); }} className="btn-ghost" data-testid="contribute-another">Submit Another</button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="contribute-page">
      <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">// Field Intel</span>
      <h1 className="font-display text-3xl md:text-5xl mt-2 text-[var(--t-text)] uppercase mb-3 leading-tight">Contribute a Tip</h1>
      <p className="text-sm md:text-base text-[var(--t-text-dim)] mb-7 md:mb-8 max-w-2xl">Spotted a strategy, an exploit, or a missing entry? Send it to the war room. All submissions are moderated before publishing.</p>

      <form onSubmit={onSubmit} className="panel panel-glow p-5 md:p-7 space-y-5" data-testid="contribute-form">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Your Callsign *</label>
            <input className="field" value={form.author_name} onChange={e => setForm({ ...form, author_name: e.target.value })} placeholder="Citizen_XII" data-testid="contrib-name" />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Email (optional)</label>
            <input className="field" type="email" value={form.author_email} onChange={e => setForm({ ...form, author_email: e.target.value })} placeholder="cmdr@territoria.wiki" data-testid="contrib-email" />
          </div>
        </div>

        <div>
          <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Related Entry (optional)</label>
          <select className="field" value={form.page_slug} onChange={e => setForm({ ...form, page_slug: e.target.value })} data-testid="contrib-page">
            <option value="">— general / not page specific —</option>
            {pages.map(p => <option key={p.slug} value={p.slug}>{p.category} · {p.title}</option>)}
          </select>
        </div>

        <div>
          <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Title *</label>
          <input className="field" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Best deposit strategy for Tropical regions" data-testid="contrib-title" />
        </div>

        <div>
          <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Intel *</label>
          <textarea className="field" rows={8} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} placeholder="Markdown supported. Share the tip, correction, or strategy here..." data-testid="contrib-body" />
        </div>

        {error && <div className="text-sm text-[var(--t-red)] font-mono" data-testid="contrib-error">{error}</div>}

        <div className="flex justify-end">
          <button type="submit" className="btn-primary w-full sm:w-auto !justify-center" data-testid="contrib-submit"><Send className="w-4 h-4" /> Transmit</button>
        </div>
      </form>
    </div>
  );
}
